import {
  StatusAborted,
  StatusError,
  StatusOK,
  StatusRunning,
  StatusWarning,
} from '@backstage/core-components';
import { Tooltip, Typography } from '@material-ui/core';
import { relativeTime } from './relativeTime';
import {
  describeReleaseDeployment,
  ReleaseDeployment,
} from './deploymentState';

/**
 * What became of one release, in a table cell.
 *
 * Colour carries the meaning, so the states that need attention are the ones
 * that stand out: a failed deploy is red, a release that is live but running
 * badly is amber, a release that was replaced is deliberately grey because
 * nothing is wrong with it. Absence of a deploy record is greyer still: ArgoCD
 * keeps about ten history entries and most of a feed falls off that end, so
 * "no deploy record" must not look like a problem.
 */
export const ReleaseDeploymentChip = ({
  deployment,
}: {
  deployment?: ReleaseDeployment;
}) => {
  if (!deployment) {
    return null;
  }

  const label = describeReleaseDeployment(deployment);
  const when =
    'deployedAt' in deployment && deployment.deployedAt
      ? `Deployed ${relativeTime(deployment.deployedAt)}`
      : undefined;

  const status = (() => {
    switch (deployment.status) {
      case 'live':
        if (!deployment.healthy) {
          return <StatusWarning>{label}</StatusWarning>;
        }
        // An unconfirmed outcome is not a warning: nothing is known to be wrong,
        // and most of CAP's history is in this state by construction.
        return deployment.outcomeUnknown ? (
          <StatusAborted>{label}</StatusAborted>
        ) : (
          <StatusOK>{label}</StatusOK>
        );
      case 'deploying':
        return <StatusRunning>{label}</StatusRunning>;
      case 'failed':
        return <StatusError>{label}</StatusError>;
      case 'not-deployed':
        return <StatusWarning>{label}</StatusWarning>;
      default:
        return <StatusAborted>{label}</StatusAborted>;
    }
  })();

  const explanation = (() => {
    switch (deployment.status) {
      case 'live':
        if (!deployment.healthy) {
          return `${
            when ?? 'Deployed'
          }, but the cluster reports the service as unhealthy`;
        }
        if (deployment.outcomeUnknown) {
          return `${
            when ?? 'Deployed'
          }, and it is the newest deployment, but the source recorded no steps for it so it cannot confirm the deploy finished`;
        }
        return when ?? 'This is the running version';
      case 'superseded':
        return `${when ?? 'Deployed'}, and has since been replaced${
          deployment.supersededBy ? ` by ${deployment.supersededBy}` : ''
        }`;
      case 'deploying':
        return `${when ?? 'Started'}, and the deploy has not finished yet`;
      case 'failed':
        return `${when ?? 'Attempted'}, and the sync did not succeed`;
      case 'not-deployed':
        return 'Newer than the retained deploy history and absent from it, so it never reached the cluster';
      default:
        return 'Older than the deploy history ArgoCD still keeps, so nothing can be said about it';
    }
  })();

  return (
    <Tooltip title={explanation}>
      <Typography variant="body2" component="span">
        {status}
      </Typography>
    </Tooltip>
  );
};
